// =============================================================================
// ui/tabs/AnalyticsCustom.tsx — the Custom Analytics sub-tab.
// -----------------------------------------------------------------------------
// A host for three dashboards, chosen by a picker: the freeform chart builder
// (`Build your own`) plus two curated panels. The host owns the period bar and
// the picker; each dashboard renders only its own body.
//
// ── ⚠ PARTIAL, AND THE MISSING PARTS ARE NAMED ON SCREEN ──
//
//   Build your own      ← ported, this file
//   Edge Health         ← ported, `EdgeHealth.tsx`
//   Controllable Loss   ← ported, and it is the SAME COMPONENT the Overview tab
//                         renders — see below
//
// ⚠ EDGE HEALTH WAS BUILT WITHOUT A SCREENSHOT, deliberately and narrowly: it
// has no controls at all, so the failure the screenshot-first rule guards
// against — inventing a control beta does not have — cannot happen there. 🔴 The
// side-by-side against beta is still owed and has NOT been run.
//
// ── ⚠ CONTROLLABLE LOSS IS ONE COMPONENT ON TWO SURFACES ──
//
// It is `CostOfIndisciplineCard`, imported from the Overview tab rather than
// rebuilt. That is the old client's own design: it calls ONE function from both
// places and says in its source that they "stay in sync". Two components
// measuring one fact is how a screen ends up disagreeing with the screen next
// to it — and it would mean fixing PD-22 twice.
//
// ── 🚨 THE WIDGETS ARE SAVED, AND THIS TAB DOES NOT DO THE SAVING ──
//
// They live in `preferences.app_flags` — one JSONB blob shared with the leak
// rules, every layout choice and the P&L unit, in BOTH clients. A save writes
// the whole column. `ui/state/useCustomWidgets` is the only place that writes
// it, and this file may not reach around it: no `update({ appFlags })` here, no
// local copy of the array held in state and written back later. The array is
// re-read at the moment of every write, because two edits without a reload
// between them would otherwise have the second erase the first (D141).
//
// ⚠ AND A FAILED SAVE STAYS ON SCREEN. `usePreferences` applies its patch
// locally first and leaves it applied when the write fails, so an edit that did
// not stick still looks made. The notice under the toolbar is the only thing
// that says otherwise.
//
// ── ⚠ THIS TAB HAS ITS OWN DATE RANGE AND IGNORES THE GLOBAL FILTER ──
//
// Same as every analytics sub-tab. Josh's screenshot has the header filter on
// `This week` while every chart runs Mar 30 → Aug 10, which is the tab's own
// `All`. Two range controls on one screen showing different windows is the
// class D130 is about; reading the global one here would look entirely correct.
// =============================================================================

import { useMemo } from 'react';
import { useTabMemory } from '../state/tabMemory';
import { useAccounts } from '../state/accounts';
import { useFilters } from '../state/filters';
import { usePreferences } from '../state/preferences';
import { useRange } from '../state/range';
import { useTradeRows } from '../state/useTradeRows';
import {
  DEFAULT_ANALYTICS_PERIOD, analyticsPeriodLabel, controllableLoss,
  controllableLossTrades, dateInAnalyticsPeriod, weekStartMonday,
  type AnalyticsPeriod,
} from '../../selectors/analytics';
import { tradesCountingForMetrics } from '../../selectors/accounts';
import { effectiveTradeDate } from '../../selectors/population';
import { edgeHealth, type MetricTrade } from '../../selectors/customAnalytics';
import { selectKpis } from '../../selectors/kpis';
import { useContracts } from '../state/contracts';
import { usePnlUnit } from '../state/usePnlUnit';
import { EdgeHealthPanel } from './EdgeHealth';
import { useCustomWidgets } from '../state/useCustomWidgets';
import type { DayBoundaryMode } from '../../selectors/range';
import { CostOfIndisciplineCard, PeriodBar } from './Analytics';
import { CustomWidgetCard } from './CustomWidgetCard';
import { CoachDrill, type DrillTrade } from './CoachDrill';

/** ⚠ ALL TIME. The tab's own period does the narrowing — see the header. */
const UNBOUNDED = { startDate: null, endDate: null };

interface DashboardDef { id: string; label: string; desc: string }

/** The old client's three, in its order. `freeform` is the default. */
const DASHBOARDS: readonly DashboardDef[] = [
  {
    id: 'freeform', label: 'Build your own',
    desc: 'Compose your own metric charts — line / area / bar, overlays, cumulative.',
  },
  {
    id: 'controllable', label: 'Controllable Loss',
    desc: 'The cost of indiscipline — A-game net, $-weighted leak leaderboard, clean vs dirty days.',
  },
  {
    id: 'edge', label: 'Edge Health',
    desc: 'Is the edge intact? Expectancy, profit factor, payoff, with a one-line verdict.',
  },
];

export function AnalyticsCustomTab() {
  const { accounts } = useAccounts();
  const { contracts } = useContracts();
  // Drives the Edge Health panel. The build-your-own widgets below have their
  // OWN per-metric units ($ / % / R / ×), which is a different idea and stays.
  const { unit } = usePnlUnit();
  // ⚠ `facets` deliberately NOT read — analytics does not apply them. Same as
  // Overview and Setup Quality; the reasoning is in `Analytics.tsx`.
  const { accounts: selection } = useFilters();
  const { prefs } = usePreferences();
  const { today } = useRange();
  const mode = prefs?.dayBoundaryMode ?? null;

  const [period, setPeriod] = useTabMemory<AnalyticsPeriod>('custom:period', DEFAULT_ANALYTICS_PERIOD);
  const [dash, setDash] = useTabMemory('custom:dash', 'freeform');
  const store = useCustomWidgets();

  const { phase, rows, error } = useTradeRows(UNBOUNDED, mode);

  /**
   * ⚠ `tradesCountingForMetrics`, not the raw rows — a SIM or replay account is
   * flagged exclude-from-metrics and its trades must not chart as edge. Same
   * population as every other analytics tab.
   */
  const inPeriod = useMemo<MetricTrade[] | null>(() => {
    if (!rows || !mode || !today) return null;
    const scoped = tradesCountingForMetrics(rows, selection, accounts ?? []);
    return scoped
      .filter((t) => dateInAnalyticsPeriod(effectiveTradeDate(t, mode), period, today))
      .map((t) => ({
        netPnl: t.netPnl ?? null,
        qty: t.qty ?? null,
        fees: t.fees ?? null,
        side: t.side ?? null,
        date: t.date ?? null,
        sessionDate: t.sessionDate ?? null,
        timeIn: t.timeIn ?? null,
        timeOut: t.timeOut ?? null,
        rrRealized: t.rrRealized ?? null,
        riskAtEntry: t.riskAtEntry ?? null,
      }));
  }, [rows, mode, today, selection, accounts, period]);

  /**
   * Edge Health reads the RAW rows, not the metric-trade projection above —
   * it needs the contract and the clock that `selectKpis` values a trade with.
   *
   * 🚨 NARROWED HERE, NOT LEFT TO `selectKpis`. This was WRONG and the
   * side-by-side against beta is what caught it.
   *
   * The first version passed the unnarrowed rows and reasoned that `selectKpis`
   * narrows internally, so the tiles would be right. They were. But the
   * R-multiple distribution and the weekly trend read the set they were HANDED,
   * so they described every SIM and replay trade as well — **54 extra trades on
   * Josh's book**, and the caption printed "272 of 509" as though both halves
   * came from one population. Beta says 218 of 510.
   *
   * ⚠ Narrowing twice is a no-op; handing a section a wider set is not.
   */
  const edge = useMemo(() => {
    if (!rows || !mode || !today) return null;
    const scoped = tradesCountingForMetrics(rows, selection, accounts ?? [])
      .filter((t) => dateInAnalyticsPeriod(effectiveTradeDate(t, mode), period, today));
    return edgeHealth({
      trades: scoped,
      kpisOf: (ts) => selectKpis({
        trades: ts, accounts: accounts ?? [], contracts: contracts ?? [],
        // ⚠ The real unit: `EdgeHealthPanel` below now formats in whatever this
        // returns, so leaving it on dollars would print dollar figures under a
        // points label — the one state worse than not converting at all.
        selection, unit,
      }),
      rrOf: (t) => t.rrRealized ?? null,
      sideOf: (t) => t.side ?? null,
      // ── 🔴 THIS LINE WAS WRONG TWICE, UNDER A COMMENT THAT SAID IT WAS RIGHT ──
      //
      // It read "the client's ONE week rule — Monday" and passed `weekStartOf`,
      // which is SUNDAY. And the old seam could only be handed an ISO string, so
      // the engine bucketed on the CALENDAR date — an evening trade went into
      // the week its clock fell in rather than the week its session belongs to.
      //
      // ▶ Both are decided here now, because this is the only layer that knows
      // the trader's day-boundary preference. Beta buckets analytics charts from
      // Monday (app/index.html:42592) on the effective date, and so does this.
      weekOf: (t) => weekStartMonday(effectiveTradeDate(t, mode)),
    });
  }, [rows, mode, today, period, accounts, contracts, selection, unit]);

  /**
   * ⚠ THE TAG CONFIG COMES FROM THE TRADER'S SETTINGS, exactly as on Overview.
   * A tag nobody has configured counts as controllable — the setting exists to
   * EXCLUDE, not to opt in — so passing `null` would be right only by luck.
   */
  const clConfig = useMemo(() => (
    (prefs?.appFlags?.['controllableMistakeTags'] as Record<string, boolean> | undefined) ?? null
  ), [prefs]);

  /**
   * ── 🎯 THE ROWS OPEN THE TRADES BEHIND THEM, AS ON OVERVIEW ──
   *
   * `CostOfIndisciplineCard` has always taken an `onDrill`, and this tab has
   * always rendered that card — it simply never passed one, so the identical
   * panel was clickable on Overview and inert here. Beta wires the drill on
   * BOTH (`wireControllableLossDrill`, index.html:36155, reached from the
   * `custom` branch at :43690) and its rows say "Click a row to see its trades"
   * on both.
   *
   * ⚠ THE CARD RENDERS A BUTTON ONLY WHERE ONE OPENS SOMETHING — the prop is
   * optional precisely so its absence gives plain text rather than a dead
   * control. That is why this was invisible rather than broken.
   */
  const [drill, setDrill] = useTabMemory<
    { by: { reason: string } | { date: string }; label: string } | null
  >('custom:drill', null);

  const cl = useMemo(() => {
    if (!rows || !mode || !today) return null;
    const scoped = tradesCountingForMetrics(rows, selection, accounts ?? [])
      .filter((t) => dateInAnalyticsPeriod(effectiveTradeDate(t, mode), period, today));
    return controllableLoss(scoped.map((t) => ({
      date: effectiveTradeDate(t, mode),
      timeIn: t.timeIn,
      netPnl: t.netPnl,
      booleans: t.booleans as Record<string, unknown> | null,
      mistakeTags: t.mistakeTags as readonly string[] | null,
      mfePoints: t.mfePoints,
      riskPoints: t.riskPoints,
      // ⚠ MONDAY, matching beta's analytics bucketing and the chart builder on
      // this same tab. The dates above are already the effective ones.
    })), clConfig, weekStartMonday);
  }, [rows, mode, today, selection, accounts, period, clConfig]);

  /**
   * 🚨 THE SAME FUNCTION AND THE SAME CONFIG THE CARD COUNTED WITH.
   *
   * Recomputing "which trades are controllable" here would be a SECOND copy of
   * the rule, and the first time the two disagree the reader clicks a row
   * saying 13 trades and gets a table of eleven, with nothing on screen to say
   * which is right. Overview says exactly this at its own call site and names
   * the two bugs that taught it (#88, #89).
   *
   * ⚠ `clConfig`, not null — an unconfigured tag counts AS controllable, so
   * null is wrong by luck rather than obviously wrong.
   *
   * ⚠ THE EFFECTIVE DATE, because a worst-day row is keyed by the session day.
   * Matching on the calendar date would open the wrong evening's trades — PD-17,
   * and the same line Overview gets right.
   */
  const drillTrades = useMemo(() => {
    if (!drill || !rows || !mode || !today) return null;
    const scoped = tradesCountingForMetrics(rows, selection, accounts ?? [])
      .filter((t) => dateInAnalyticsPeriod(effectiveTradeDate(t, mode), period, today))
      .map((t) => ({ ...t, date: effectiveTradeDate(t, mode) }));
    return controllableLossTrades(scoped as never, drill.by, clConfig);
  }, [drill, rows, mode, today, selection, accounts, period, clConfig]);

  const active = DASHBOARDS.find((d) => d.id === dash) ?? (DASHBOARDS[0] as DashboardDef);

  if (phase === 'failed') {
    return <div className="card"><h3>Could not load trades</h3><p className="muted">{error}</p></div>;
  }
  if (!inPeriod || !mode) {
    return <div className="card"><p className="muted">Loading…</p></div>;
  }

  return (
    <div className="analytics-tab">
      <PeriodBar period={period} onPick={setPeriod} />

      <div className="cw-dashbar">
        <span className="cw-dashlabel">Dashboard</span>
        {DASHBOARDS.map((d) => (
          <button
            key={d.id} type="button"
            className={`period-chip${d.id === active.id ? ' active' : ''}`}
            title={d.desc}
            aria-pressed={d.id === active.id}
            onClick={() => setDash(d.id)}
          >
            {d.label}
          </button>
        ))}
      </div>

      {active.id === 'freeform' && (
        <Freeform store={store} trades={inPeriod} mode={mode} />
      )}

      {active.id === 'edge' && (
        edge
          ? <EdgeHealthPanel model={edge} />
          : (
            <div className="card">
              <h3>No trades in {analyticsPeriodLabel(period)}</h3>
              <p className="muted">Widen the period above to read the edge over more history.</p>
            </div>
          )
      )}

      {active.id === 'controllable' && cl && (
        <CostOfIndisciplineCard cl={cl} onDrill={(by, label) => setDrill({ by, label })} />
      )}

      {/* ⚠ `onChanged` IS A NO-OP, for the same reason it is on Overview: this
          tab's data comes from the range read the shell owns, so a save inside
          the drill leaves nothing here to re-read. The Coach's Report reloads
          because its own leak computation changes; this does not. */}
      {drill && drillTrades ? (
        <CoachDrill
          label={drill.label}
          trades={drillTrades as unknown as DrillTrade[]}
          column={null}
          onClose={() => setDrill(null)}
          onChanged={() => {}}
        />
      ) : null}
    </div>
  );
}

function Freeform(
  { store, trades, mode }: {
    store: ReturnType<typeof useCustomWidgets>;
    trades: readonly MetricTrade[];
    mode: DayBoundaryMode;
  },
) {
  const { widgets, canWrite, busy, error, add, remove, replace } = store;

  return (
    <>
      <div className="cw-toolbar">
        <span className="grow" />
        <button
          type="button" className="btn"
          disabled={!canWrite || busy}
          onClick={() => { void add(); }}
        >
          + Add widget
        </button>
      </div>

      <p className="muted cw-blurb">
        Build your own charts — pick any metric, set each to <b>line / area / bar</b>{' '}
        (∿ ◢ ▮), choose Day/Week/Month, <b>Σ</b> for cumulative, <b>L/S</b> to split
        long vs short, <b>+ overlay</b> to stack metrics on one card. Foundational
        stats, your way; the curated cards stay the deep read.
      </p>

      {/* 🚨 The ONLY thing that says an edit did not stick — see the header. */}
      {error && <p className="cw-unsaved">⚠ {error}</p>}

      <div className="cw-grid">
        {widgets.map((w, i) => (
          <CustomWidgetCard
            key={i}
            widget={w}
            trades={trades}
            mode={mode}
            onChange={(next) => { void replace(i, next); }}
            onRemove={() => { void remove(i); }}
          />
        ))}
        <button
          type="button" className="cw-add-tile"
          disabled={!canWrite || busy}
          onClick={() => { void add(); }}
        >
          <span className="cw-add-plus">+</span>
          <span>Add widget</span>
        </button>
      </div>
    </>
  );
}
